/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type FunctionComponent, useCallback, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { NavigationAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import AkModalDialog, {
	ModalTransition as AkModalTransition,
	ModalBody,
} from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';

import {
	ANALYTICS_CHANNEL,
	DEFAULT_TEST_ID,
	MODAL_DIALOG_TEST_ID,
	TRIGGER_ELEMENT_TEST_ID,
} from '../../constants';
import type { GlobalInviteProps, ModalDialogContentProps } from '../../types';

import { messages } from './messages';

// eslint-disable-next-line @typescript-eslint/ban-types
export function withModalDialog(Content: FunctionComponent<ModalDialogContentProps>) {
	return function ModalDialog({
		testId = DEFAULT_TEST_ID,
		triggerElement: TriggerElement,
		children,
		...props
	}: GlobalInviteProps) {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { formatMessage } = useIntl();

		const [isModalDialogOpen, setIsModalDialogOpen] = useState<boolean>(false);

		const openModalDialog = useCallback(() => {
			if (fg('platform_global-invite-analytics')) {
				createAnalyticsEvent({
					eventType: UI_EVENT_TYPE,
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'globalInviteButton',
					attributes: {
						componentName: 'globalInvite',
						product: props.product,
					},
				}).fire(ANALYTICS_CHANNEL);
			}

			setIsModalDialogOpen(true);
		}, [createAnalyticsEvent, props.product]);

		const closeModalDialog = useCallback(() => {
			setIsModalDialogOpen(false);
		}, []);

		const focusDialog = useCallback((node: HTMLElement) => {
			// Focus the dialog's container when it finishes opening
			node.focus();
		}, []);

		if (fg('platform_global-invite-analytics')) {
			return (
				<NavigationAnalyticsContext data={{}}>
					<div data-testid={testId}>
						{TriggerElement ? (
							<TriggerElement onClick={openModalDialog} />
						) : (
							<Button
								shouldFitContainer
								onClick={openModalDialog}
								testId={`${testId}--${TRIGGER_ELEMENT_TEST_ID}`}
							>
								{children}
							</Button>
						)}

						<AkModalTransition>
							{isModalDialogOpen ? (
								<AkModalDialog
									width="small"
									shouldScrollInViewport
									shouldReturnFocus
									onClose={closeModalDialog}
									onOpenComplete={focusDialog}
									label={formatMessage(messages.modalDialogLabel)}
									testId={`${testId}--${MODAL_DIALOG_TEST_ID}`}
								>
									<ModalBody>
										<Content closeModalDialog={closeModalDialog} {...props} />
									</ModalBody>
								</AkModalDialog>
							) : null}
						</AkModalTransition>
					</div>
				</NavigationAnalyticsContext>
			);
		}

		return (
			<div data-testid={testId}>
				{TriggerElement ? (
					<TriggerElement onClick={openModalDialog} />
				) : (
					<Button
						shouldFitContainer
						onClick={openModalDialog}
						testId={`${testId}--${TRIGGER_ELEMENT_TEST_ID}`}
					>
						{children}
					</Button>
				)}

				<AkModalTransition>
					{isModalDialogOpen ? (
						<AkModalDialog
							width="small"
							shouldScrollInViewport
							shouldReturnFocus
							onClose={closeModalDialog}
							onOpenComplete={focusDialog}
							label={formatMessage(messages.modalDialogLabel)}
							testId={`${testId}--${MODAL_DIALOG_TEST_ID}`}
						>
							<ModalBody>
								<Content closeModalDialog={closeModalDialog} {...props} />
							</ModalBody>
						</AkModalDialog>
					) : null}
				</AkModalTransition>
			</div>
		);
	};
}
